import { useState, useMemo, useCallback, useEffect } from "react";
import { format, addDays } from "date-fns";
import { cn, getColorById, formatTime, checkShiftOverlap, employeeHasShiftOnDate, calculateTotalHours, getCurrencySymbol, calculateShiftCost, getSalaryDailyCost, getSalaryWeeklyCost, isPastDate, calculatePaidAbsenceDailyCost, getEffectiveHoursForDate } from "../lib/utils";
import ShiftEditDialog from "./ShiftEditDialog";
import CopySingleShiftDialog from "./CopySingleShiftDialog";
import DeleteShiftConfirmDialog from "./DeleteShiftConfirmDialog";
import EmployeeDetailsPopover from "./EmployeeDetailsPopover";
import WeekViewEventsRow from "./WeekViewEventsRow";
import { ScrollArea, ScrollBar } from "./ui/scroll-area";
import { Badge } from "./ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./ui/tooltip";
import { AlertTriangle, Lock, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import ShiftContextMenu from "./ShiftContextMenu";

const WeekView = ({ 
  weekStart, 
  employees, 
  shifts, 
  absences = [],
  events = [],
  businessHours,
  hoursOverrides = [],
  breakTypes = [],
  shiftPatterns = [],
  snapInterval = 15,
  allowMultipleShifts = true,
  shiftGapMinutes = 0,
  currency = "GBP",
  rowHeight = 48,
  showStats = true,
  showCosts = true,
  scrollable = false,
  onCreateShift, 
  onUpdateShift, 
  onDeleteShift 
}) => {
  const [editingShift, setEditingShift] = useState(null);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [editingDate, setEditingDate] = useState(null);
  const [isSmallScreen, setIsSmallScreen] = useState(false);

  // Copy shift dialog state
  const [isCopyDialogOpen, setIsCopyDialogOpen] = useState(false);
  const [copyShiftData, setCopyShiftData] = useState(null);
  
  // Delete confirmation dialog state
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [deleteShiftData, setDeleteShiftData] = useState(null);

  // Check for small screen to force stacked time display
  useEffect(() => {
    const checkScreenSize = () => {
      setIsSmallScreen(window.innerWidth < 1024);
    };
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  // Generate week dates with override info
  const weekDates = useMemo(() => {
    const dates = [];
    for (let i = 0; i < 7; i++) {
      dates.push(addDays(weekStart, i));
    }
    return dates;
  }, [weekStart]);

  // Helper to check if an employee is absent on a specific date
  const getEmployeeAbsence = useCallback((employeeId, date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return absences.find(a => 
      a.employee_id === employeeId && 
      a.start_date <= dateStr && 
      a.end_date >= dateStr
    );
  }, [absences]);

  // Get absence reason label
  const getAbsenceLabel = (absence) => {
    if (!absence) return "";
    if (absence.reason_type === "custom") return absence.custom_reason;
    const labels = {
      holiday: "Hol",
      sick: "Sick",
      maternity: "Mat",
      paternity: "Pat",
      bereavement: "Ber",
      training: "Train"
    };
    return labels[absence.reason_type] || absence.reason_type;
  };

  // Get effective business hours for a day (with override support)
  const getHoursForDay = useCallback((date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return getEffectiveHoursForDate(dateStr, businessHours, hoursOverrides);
  }, [businessHours, hoursOverrides]);

  // Handle cell click to create/edit shift
  const handleCellClick = useCallback((employee, date) => {
    const dayHours = getHoursForDay(date);
    
    // Check if this date is locked (in the past)
    const isDateLocked = isPastDate(date);
    
    // Check if employee is absent on this date
    const absence = getEmployeeAbsence(employee.id, date);
    
    if (!dayHours || !dayHours.is_open) return;

    const dateStr = format(date, 'yyyy-MM-dd');
    const employeeShiftsOnDate = shifts.filter(
      s => s.employee_id === employee.id && s.date === dateStr
    );

    // If date is locked, only allow viewing existing shifts
    if (isDateLocked) {
      if (employeeShiftsOnDate.length === 0) {
        toast.error("Past rotas are locked and cannot be modified.");
        return;
      }
      // Allow viewing first shift in read-only mode
      setEditingShift(employeeShiftsOnDate[0]);
      setEditingEmployee(employee);
      setEditingDate(date);
      setIsDialogOpen(true);
      return;
    }

    // If employee is absent, don't allow creating shifts
    if (absence) {
      const absenceLabel = getAbsenceLabel(absence);
      toast.error(`${employee.name} is on ${absenceLabel} on this date.`);
      return;
    }

    // If there's one shift and multiple shifts not allowed, edit that shift
    if (employeeShiftsOnDate.length === 1) {
      setEditingShift(employeeShiftsOnDate[0]);
    } else if (employeeShiftsOnDate.length > 1) {
      // Multiple shifts - just create new (overlap check will handle it)
      setEditingShift(null);
    } else {
      setEditingShift(null);
    }
    
    // Check if employee already has a shift (if multiple shifts disabled)
    if (!allowMultipleShifts && employeeShiftsOnDate.length > 0 && !editingShift) {
      // If clicking on empty cell but employee has shift, edit the existing one
      setEditingShift(employeeShiftsOnDate[0]);
    }
    
    setEditingEmployee(employee);
    setEditingDate(date);
    setIsDialogOpen(true);
  }, [shifts, allowMultipleShifts, businessHours, getEmployeeAbsence]);

  const handleSaveShift = useCallback((times) => {
    if (!editingDate || !editingEmployee) return;
    
    const dateStr = format(editingDate, 'yyyy-MM-dd');
    const gapText = shiftGapMinutes > 0 
      ? `Shifts must be at least ${shiftGapMinutes} minutes apart`
      : "Shift would overlap with an existing shift";
    
    if (editingShift) {
      // Check for overlap when updating
      const overlap = checkShiftOverlap(
        shifts, 
        editingShift.employee_id, 
        editingShift.date, 
        times.start_time, 
        times.end_time, 
        editingShift.id,
        shiftGapMinutes
      );
      
      if (overlap) {
        toast.error(gapText);
        return;
      }
      
      onUpdateShift(editingShift.id, times);
    } else {
      // Check if employee already has a shift (if multiple shifts disabled)
      if (!allowMultipleShifts) {
        const existingShift = employeeHasShiftOnDate(shifts, editingEmployee.id, dateStr);
        if (existingShift) {
          toast.error("Employee already has a shift on this day");
          return;
        }
      }
      
      // Check for overlap when creating
      const overlap = checkShiftOverlap(
        shifts, 
        editingEmployee.id, 
        dateStr, 
        times.start_time, 
        times.end_time,
        null,
        shiftGapMinutes
      );
      
      if (overlap) {
        toast.error(gapText);
        return;
      }
      
      onCreateShift({
        employee_id: editingEmployee.id,
        date: dateStr,
        ...times,
      });
    }
    setIsDialogOpen(false);
  }, [editingDate, editingEmployee, editingShift, onCreateShift, onUpdateShift, shifts, allowMultipleShifts, shiftGapMinutes]);

  const handleDeleteShift = useCallback(() => {
    if (editingShift) {
      onDeleteShift(editingShift.id);
    }
    setIsDialogOpen(false);
  }, [editingShift, onDeleteShift]);

  // Context menu handlers
  const handleContextEdit = useCallback((shift, employee) => {
    // Find the date from the shift
    const shiftDate = new Date(shift.date + 'T00:00:00');
    setEditingShift(shift);
    setEditingEmployee(employee);
    setEditingDate(shiftDate);
    setIsDialogOpen(true);
  }, []);

  const handleContextCopy = useCallback((shift, employee) => {
    setCopyShiftData({ shift, employee });
    setIsCopyDialogOpen(true);
  }, []);

  const handleContextDelete = useCallback((shift, employee) => {
    setDeleteShiftData({ shift, employee });
    setIsDeleteDialogOpen(true);
  }, []);

  // Handle copy shift
  const handleCopyShift = useCallback(async ({ shift, targetEmployeeId, targetDate }) => {
    try {
      await onCreateShift({
        employee_id: targetEmployeeId,
        date: targetDate,
        start_time: shift.start_time,
        end_time: shift.end_time,
        breaks: shift.breaks || [],
        rate_name: shift.rate_name,
        rate_segments: shift.rate_segments || [],
      });
      toast.success("Shift copied successfully");
    } catch (error) {
      console.error("Failed to copy shift:", error);
      toast.error("Failed to copy shift");
      throw error;
    }
  }, [onCreateShift]);

  // Handle delete shift confirmation
  const handleDeleteShiftConfirm = useCallback((shift) => {
    onDeleteShift(shift.id);
    toast.success("Shift deleted");
  }, [onDeleteShift]);

  // Get shift for employee on date
  const getShifts = (employeeId, date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return shifts.filter(s => s.employee_id === employeeId && s.date === dateStr);
  };

  // Get day hours for dialog
  const dialogDayHours = editingDate 
    ? getHoursForDay(editingDate)
    : { open_time: "10:00", close_time: "23:00" };

  // Get employee shifts for dialog
  const dialogEmployeeShifts = useMemo(() => {
    if (!editingEmployee || !editingDate) return [];
    const dateStr = format(editingDate, 'yyyy-MM-dd');
    return shifts.filter(s => s.employee_id === editingEmployee.id && s.date === dateStr);
  }, [editingEmployee, editingDate, shifts]);

  return (
    <div className="flex" data-testid="week-view">
      {/* Main scrollable area */}
      <div className="flex-1 overflow-hidden">
        <ScrollArea className="w-full">
          <div className="min-w-[800px]">
            {/* Header Row */}
            <div className="flex border-b border-border bg-muted/30 h-[52px]">
              <div className="w-56 flex-shrink-0 px-2 border-r border-border flex items-center">
                <span className="text-sm font-medium text-muted-foreground">Employee</span>
              </div>
              {weekDates.map((date, index) => {
                const dayHours = getHoursForDay(date);
                const isToday = format(date, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd');
                const isDateLocked = isPastDate(date);
                const hasOverride = dayHours?.override;

                return (
                  <div
                    key={index}
                    className={cn(
                      "flex-1 px-2 border-r border-border text-center min-w-[100px] flex flex-col items-center justify-center overflow-hidden",
                      (!dayHours || !dayHours.is_open) && "bg-muted/50 opacity-60",
                      isDateLocked && dayHours?.is_open && "bg-muted/30"
                    )}
                  >
                    <div className="flex items-center gap-1">
                      {hasOverride && dayHours?.is_open && (
                        <CalendarClock className="w-3 h-3 text-primary flex-shrink-0" title={hasOverride.name} />
                      )}
                      {isDateLocked && dayHours?.is_open && (
                        <Lock className="w-3 h-3 text-muted-foreground flex-shrink-0" />
                      )}
                      <p className={cn(
                        "text-sm font-medium leading-tight",
                        isToday && "text-primary"
                      )}>
                        {format(date, 'EEE')}
                      </p>
                    </div>
                    <p className={cn(
                      "text-xs text-muted-foreground leading-tight",
                      isToday && "text-primary font-medium"
                    )}>
                      {format(date, 'MMM d')}
                    </p>
                    {(!dayHours || !dayHours.is_open) && (
                      <span className="text-[10px] text-muted-foreground truncate max-w-full" title={hasOverride?.name}>
                        {hasOverride ? hasOverride.name : "Closed"}
                      </span>
                    )}
                    {hasOverride && dayHours?.is_open && (
                      <span className="text-[10px] text-primary whitespace-nowrap">{hasOverride.open_time}-{hasOverride.close_time}</span>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Events Row - Above employee rows */}
            {events.length > 0 && (
              <WeekViewEventsRow
                events={events}
                weekDates={weekDates}
                rowHeight={32}
              />
            )}

            {/* Employee Rows */}
            {employees.length === 0 ? (
              <div className="flex items-center justify-center h-32 text-muted-foreground">
                <p>No employees added yet. Go to Employees tab to add staff.</p>
              </div>
          ) : (
            <TooltipProvider>
              {employees.map((employee) => {
                const color = getColorById(employee.color);
                const currencySymbol = getCurrencySymbol(currency);
                
                // Calculate weekly totals for this employee
                const employeeWeekShifts = shifts.filter(s => s.employee_id === employee.id);
                const totalHours = calculateTotalHours(employeeWeekShifts);
                const totalCost = employeeWeekShifts.reduce((acc, shift) => {
                  return acc + calculateShiftCost(shift.start_time, shift.end_time, 0, shift, breakTypes, employee);
                }, 0);
                
                // Check if below minimum contracted hours
                const isBelowMinimum = employee.has_contracted_hours && totalHours < (employee.min_hours || 0);

                // On small screens, enforce minimum row height for stacked times
                const effectiveRowHeight = isSmallScreen ? Math.max(rowHeight, 48) : rowHeight;

                return (
                  <div key={employee.id} className="flex border-b border-border" style={{ height: `${effectiveRowHeight}px` }}>
                    {/* Employee Info */}
                    <EmployeeDetailsPopover employee={employee} currency={currency}>
                      <div 
                        className="w-56 flex-shrink-0 px-2 border-r border-border bg-card sticky left-0 z-10 flex items-center cursor-pointer hover:bg-accent/30 transition-colors"
                        data-testid={`employee-info-${employee.id}`}
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          {employee.avatar_url ? (
                            <img 
                              src={employee.avatar_url} 
                              alt={employee.name}
                              className={cn(
                                "rounded-full object-cover flex-shrink-0 border border-border",
                                effectiveRowHeight <= 48 ? "w-7 h-7" : "w-10 h-10"
                              )}
                            />
                          ) : (
                            <div 
                              className={cn(
                                "rounded-full flex items-center justify-center font-semibold flex-shrink-0",
                                effectiveRowHeight <= 48 ? "w-7 h-7 text-xs" : "w-10 h-10 text-sm",
                                !color.isCustom && color.avatar
                              )}
                              style={color.isCustom ? { backgroundColor: employee.color + '20', color: employee.color } : {}}
                            >
                              {employee.name.charAt(0).toUpperCase()}
                            </div>
                          )}
                          <div className="overflow-hidden min-w-0">
                            <p className="font-medium text-sm truncate leading-tight">{employee.name}</p>
                            {effectiveRowHeight > 44 && (
                              <p className="text-xs text-muted-foreground truncate leading-tight">{employee.role}</p>
                            )}
                            {effectiveRowHeight > 60 && employee.hourly_rate > 0 && (
                              <p className="text-xs text-muted-foreground tabular-nums leading-tight">
                                {currencySymbol}{employee.hourly_rate.toFixed(2)}/hr
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    </EmployeeDetailsPopover>

                    {/* Day Cells */}
                    {weekDates.map((date, index) => {
                      const dayHours = getHoursForDay(date);
                      const employeeShifts = getShifts(employee.id, date);
                      const isDateLocked = isPastDate(date);
                      const absence = getEmployeeAbsence(employee.id, date);
                      const hasOverride = dayHours?.override;

                      return (
                        <div
                          key={index}
                          className={cn(
                            "flex-1 px-1 py-1 border-r border-border min-w-[100px] week-cell flex flex-col",
                            (!dayHours || !dayHours.is_open) && "day-closed cursor-not-allowed",
                            absence && "cursor-not-allowed",
                            dayHours?.is_open && !isDateLocked && !absence && "cursor-pointer",
                            dayHours?.is_open && isDateLocked && employeeShifts.length === 0 && "cursor-not-allowed",
                            dayHours?.is_open && isDateLocked && employeeShifts.length > 0 && "cursor-pointer",
                            isDateLocked && "bg-muted/30"
                          )}
                          style={{ height: `${effectiveRowHeight}px` }}
                          onClick={() => handleCellClick(employee, date)}
                          data-testid={`week-cell-${employee.id}-${format(date, 'yyyy-MM-dd')}`}
                        >
                          {/* Show absence indicator */}
                          {absence ? (
                            <div className="flex-1 flex items-center justify-center bg-muted/60 rounded border border-dashed border-muted-foreground/30">
                              <div className="text-center">
                                <p className="text-xs font-medium text-muted-foreground">
                                  {getAbsenceLabel(absence)}
                                </p>
                                {absence.is_paid && (
                                  <span className="text-[10px] text-green-600 dark:text-green-400">(Paid)</span>
                                )}
                              </div>
                            </div>
                          ) : employeeShifts.length > 0 ? (
                            <div className="flex flex-col gap-1 w-full h-full py-0.5">
                              {employeeShifts.map((shift) => (
                                <ShiftContextMenu
                                  key={shift.id}
                                  shift={shift}
                                  employee={employee}
                                  onEdit={handleContextEdit}
                                  onCopy={handleContextCopy}
                                  onDelete={handleContextDelete}
                                  isLocked={isDateLocked}
                                >
                                  <div 
                                    className={cn(
                                      "flex-1 px-1 rounded border flex items-center justify-center cursor-pointer",
                                      color.bg,
                                      color.border,
                                      isDateLocked && "opacity-75"
                                    )}
                                  >
                                    {(rowHeight > 50 || isSmallScreen) ? (
                                      <div className="text-center">
                                        <p className={cn("text-xs tabular-nums font-medium leading-none", color.text)}>
                                          {formatTime(shift.start_time)}
                                        </p>
                                        <p className={cn("text-xs tabular-nums leading-none mt-0.5", color.text)}>
                                          {formatTime(shift.end_time)}
                                        </p>
                                      </div>
                                    ) : (
                                      <p className={cn("text-[10px] tabular-nums font-medium leading-none whitespace-nowrap", color.text)}>
                                        {formatTime(shift.start_time)} - {formatTime(shift.end_time)}
                                      </p>
                                    )}
                                  </div>
                                </ShiftContextMenu>
                              ))}
                            </div>
                          ) : (
                            dayHours.is_open && rowHeight > 50 && !isDateLocked && (
                              <div className="flex-1 flex items-center justify-center">
                                <span className="text-muted-foreground/50 text-xs">+</span>
                              </div>
                            )
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </TooltipProvider>
          )}
        </div>
        <ScrollBar orientation="horizontal" />
      </ScrollArea>
      </div>

      {/* Fixed Stats Column */}
      {showStats && (
        <TooltipProvider>
          <div className="w-28 flex-shrink-0 border-l border-border bg-muted/30">
            {/* Stats Header */}
            <div className="h-[52px] flex items-center justify-center border-b border-border bg-muted/50">
              <span className="text-sm font-medium text-muted-foreground">Total</span>
            </div>
            {/* Stats Rows */}
            {employees.map((employee) => {
              const employeeWeekShifts = shifts.filter(s => s.employee_id === employee.id);
              const totalHours = calculateTotalHours(employeeWeekShifts);
              
              const isSalaryEmployee = employee.pay_type === "salary";
              let totalCost = 0;
              let costType = null; // 'shift', 'salary', 'paid_leave', or null
              let unpaidDays = 0;
              
              // Helper to check unpaid leave for a date
              const isOnUnpaidLeave = (date) => {
                const dateStr = format(date, 'yyyy-MM-dd');
                return absences.some(a => 
                  a.employee_id === employee.id && 
                  !a.is_paid && 
                  a.start_date <= dateStr && 
                  a.end_date >= dateStr
                );
              };
              
              if (isSalaryEmployee) {
                // Salary employees: add daily cost for each day NOT on unpaid leave
                weekDates.forEach(date => {
                  if (isOnUnpaidLeave(date)) {
                    unpaidDays++;
                  } else {
                    totalCost += getSalaryDailyCost(employee);
                  }
                });
                costType = unpaidDays < 7 ? 'salary' : null;
              } else {
                // Hourly employees: add shift costs
                totalCost = employeeWeekShifts.reduce((acc, shift) => {
                  return acc + calculateShiftCost(shift.start_time, shift.end_time, 0, shift, breakTypes, employee);
                }, 0);
                if (employeeWeekShifts.length > 0) costType = 'shift';
                
                // Add paid absence costs for days without shifts
                weekDates.forEach(date => {
                  const dateStr = format(date, 'yyyy-MM-dd');
                  const absence = getEmployeeAbsence(employee.id, date);
                  if (absence && absence.is_paid) {
                    const hasShiftOnDay = employeeWeekShifts.some(s => s.date === dateStr);
                    if (!hasShiftOnDay) {
                      totalCost += calculatePaidAbsenceDailyCost(absence, employee);
                      if (!costType) costType = 'paid_leave';
                    }
                  }
                });
              }
              
              const isBelowMinimum = employee.has_contracted_hours && totalHours < (employee.min_hours || 0);
              const currencySymbol = getCurrencySymbol(currency);
              const hasCost = totalCost > 0;
              const hasUnpaidLeave = unpaidDays > 0;
              
              // Match the effectiveRowHeight from employee rows
              const effectiveRowHeight = isSmallScreen ? Math.max(rowHeight, 48) : rowHeight;

              return (
                <div 
                  key={employee.id} 
                  className={cn(
                    "flex items-center justify-center border-b border-border",
                    isBelowMinimum && "bg-amber-50 dark:bg-amber-950/30"
                  )}
                  style={{ height: `${effectiveRowHeight}px` }}
                >
                  <div className="space-y-0.5 text-center">
                    {employeeWeekShifts.length > 0 ? (
                      <span className="text-xs tabular-nums font-medium">{totalHours.toFixed(2)}h</span>
                    ) : isSalaryEmployee && !hasUnpaidLeave ? (
                      <span className="text-xs tabular-nums text-muted-foreground">(Salary)</span>
                    ) : isSalaryEmployee && hasUnpaidLeave && unpaidDays < 7 ? (
                      <span className="text-xs tabular-nums text-muted-foreground">(Partial)</span>
                    ) : costType === 'paid_leave' ? (
                      <span className="text-xs tabular-nums text-muted-foreground">(Paid)</span>
                    ) : hasUnpaidLeave ? (
                      <span className="text-xs tabular-nums text-muted-foreground">(Unpaid)</span>
                    ) : (
                      <span className="text-xs tabular-nums text-muted-foreground">-</span>
                    )}
                    {showCosts && effectiveRowHeight > 50 && hasCost && (
                      <p className={cn(
                        "text-xs tabular-nums font-medium",
                        costType === 'paid_leave' ? "text-green-600 dark:text-green-400" : "text-primary"
                      )}>
                        {currencySymbol}{totalCost.toFixed(2)}
                      </p>
                    )}
                    {isBelowMinimum && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className="flex items-center justify-center gap-1 text-amber-600 dark:text-amber-400">
                            <AlertTriangle className="w-3 h-3" />
                            {effectiveRowHeight > 50 && <span className="text-xs font-medium">Low</span>}
                          </div>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Scheduled {totalHours.toFixed(2)}h but minimum is {employee.min_hours}h/week</p>
                        </TooltipContent>
                      </Tooltip>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </TooltipProvider>
      )}

      {/* Edit Dialog */}
      <ShiftEditDialog
        open={isDialogOpen}
        onOpenChange={setIsDialogOpen}
        shift={editingShift}
        employee={editingEmployee}
        employeeShifts={dialogEmployeeShifts}
        breakTypes={breakTypes}
        shiftPatterns={shiftPatterns}
        onSave={handleSaveShift}
        onDelete={handleDeleteShift}
        businessHours={dialogDayHours}
        snapInterval={snapInterval}
        shiftGapMinutes={shiftGapMinutes}
        currency={currency}
        isLocked={editingDate ? isPastDate(editingDate) : false}
      />

      {/* Copy Shift Dialog */}
      <CopySingleShiftDialog
        open={isCopyDialogOpen}
        onOpenChange={(open) => {
          setIsCopyDialogOpen(open);
          if (!open) {
            setCopyShiftData(null);
          }
        }}
        shift={copyShiftData?.shift}
        sourceEmployee={copyShiftData?.employee}
        employees={employees}
        onCopy={handleCopyShift}
      />

      {/* Delete Shift Confirmation Dialog */}
      <DeleteShiftConfirmDialog
        open={isDeleteDialogOpen}
        onOpenChange={(open) => {
          setIsDeleteDialogOpen(open);
          if (!open) {
            setDeleteShiftData(null);
          }
        }}
        shift={deleteShiftData?.shift}
        employee={deleteShiftData?.employee}
        onConfirm={handleDeleteShiftConfirm}
      />
    </div>
  );
};

export default WeekView;
